import {
  formatOdds,
  kickoffTime,
  localizeLeague,
  resolveApiBase,
  SportsApiClient,
  todayKeyIn,
  type OddsFormat,
  type WidgetProps,
} from '@sports/core'
import { Fragment } from 'preact'
import { useState } from 'preact/hooks'
import { useFeed } from '../../use-feed'
import { useLabels } from '../../use-labels'
import { useTimeZone } from '../../use-time-zone'
import { TeamCrest, teamCrestStyles } from './team-crest'

export const MATCH_CENTER_ATTRIBUTES = [
  'league',
  'lang',
  'heading',
  'refresh',
  'api-base',
  'theme',
  'sport',
] as const

interface MatchCenterProps extends WidgetProps {
  league?: string
  lang?: string
  heading?: string
  refresh?: string
  apiBase?: string
  /** 'football' (default, betbrothers v2, 1X2) or 'basketball' (basket-API, 2-way). */
  sport?: string
}

// Flashscore-dense match center: a date bar to move day by day, matches grouped
// under the league header, crest rows with score/time and a live indicator, and
// 1X2 odds + our per-locale bookmaker on the right. Football (betbrothers v2)
// is the reference source; the layout generalises to the other sports.
export const matchCenterStyles = teamCrestStyles + `
  /* The embed page provides its own H2 ("Calendario y partidos · X"), so the
     widget's own title bar is redundant — hide it. */
  .sports-card > .sports-head { display: none; }
  .bar {
    display: flex; align-items: center; gap: 0.5rem; padding: 0.6rem 1rem;
    border-bottom: 1px solid var(--sports-border); position: relative;
  }
  .bar button {
    border: 1px solid var(--sports-border); background: var(--sports-bg);
    color: var(--sports-fg); border-radius: 8px; padding: 0.3rem 0.6rem;
    cursor: pointer; font: inherit; line-height: 1;
  }
  .bar button:disabled { opacity: 0.4; cursor: default; }
  .datebtn { display: flex; align-items: center; gap: 0.4rem; font-weight: 500; color: var(--sports-muted); }
  .datebtn svg { width: 15px; height: 15px; flex: none; }
  .bar .day { font-weight: 600; text-align: center; flex: 1; }
  /* Lovable-style controls: a "Todas las ligas" league dropdown on the left and
     a ‹ [📅 Todas las fechas] › date navigator on the right. */
  .lg-select {
    border: 1px solid var(--sports-border); background: var(--sports-bg);
    color: var(--sports-fg); border-radius: 8px; padding: 0.35rem 1.8rem 0.35rem 0.7rem;
    font: inherit; font-weight: 500; cursor: pointer; min-width: 9rem;
    appearance: none; -webkit-appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2.5'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");
    background-repeat: no-repeat; background-position: right 0.6rem center;
  }
  /* Date navigator centered in the bar; the league dropdown (when shown) sits at
     the left and the results toggle is pinned right, so the navigator stays
     centered whether or not the dropdown is present. */
  .datenav { display: flex; align-items: center; gap: 0.4rem; margin: 0 auto; }
  .datenav .nav { min-width: 2rem; color: var(--sports-muted); font-size: 1rem; }
  .toggle { position: absolute; right: 1rem; top: 50%; transform: translateY(-50%); display: flex; align-items: center; gap: 0.35rem; font-size: 0.78rem; color: var(--sports-muted); cursor: pointer; }
  /* Month date-picker popup. */
  .picker {
    position: absolute; top: calc(100% + 4px); left: 1rem; z-index: 20;
    background: var(--sports-card); border: 1px solid var(--sports-border); border-radius: 10px;
    box-shadow: 0 8px 24px rgba(0,0,0,0.18); padding: 0.6rem; width: 17rem;
  }
  .pk-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.4rem; font-weight: 700; }
  .pk-head span { text-transform: capitalize; }
  .pk-grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 2px; }
  .pk-dow span { text-align: center; font-size: 0.66rem; color: var(--sports-muted); padding: 0.2rem 0; text-transform: capitalize; }
  .pk-day {
    aspect-ratio: 1; display: flex; align-items: center; justify-content: center;
    border: 0 !important; background: transparent !important; border-radius: 6px !important;
    font-size: 0.82rem; color: var(--sports-muted); padding: 0 !important;
  }
  .pk-day.out { opacity: 0.35; }
  .pk-day.has { color: var(--sports-fg); font-weight: 600; }
  .pk-day.has:hover { background: var(--sports-muted-bg) !important; }
  .pk-day.sel { background: var(--sports-accent) !important; color: #fff !important; }
  .pk-all { width: 100%; margin-top: 0.5rem; font-size: 0.8rem; }
  .league {
    display: flex; align-items: center; justify-content: space-between;
    padding: 0.45rem 1rem; background: color-mix(in srgb, var(--sports-fg) 4%, transparent);
    font-size: 0.74rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.03em;
    color: var(--sports-muted);
  }
  .league .lg { display: flex; align-items: center; gap: 0.45rem; }
  .league .lg::before { content: ''; width: 4px; height: 13px; border-radius: 2px; background: #e11d2a; }
  .league .lg img { height: 16px; width: 16px; object-fit: contain; }
  .live-badge { color: var(--sports-live); font-size: 0.72rem; font-weight: 700; }
  .row {
    display: grid; grid-template-columns: 3rem 1fr auto auto; gap: 0 0.7rem;
    align-items: center; padding: 0.45rem 1rem; border-top: 1px solid var(--sports-border);
    font-size: 0.875rem;
  }
  .row .bk { display: flex; align-items: center; justify-content: flex-end; min-width: 4.6rem; }
  .row .bk img { height: 18px; max-width: 96px; object-fit: contain; }
  .row.live { background: color-mix(in srgb, var(--sports-live) 6%, transparent); }
  .time { text-align: center; color: var(--sports-muted); font-size: 0.78rem; white-space: nowrap; }
  .time .st { color: var(--sports-live); font-weight: 600; }
  /* Fixture: both teams hug the score / "vs" in the middle (the betbrothers.es
     look) instead of sitting at the far edges of the row — home packs to the
     right of its column with its crest inboard, away packs to the left. */
  .fx { display: grid; grid-template-columns: 1fr auto 1fr; align-items: center; gap: 0.5rem; min-width: 0; }
  .side { display: flex; align-items: center; gap: 0.45rem; min-width: 0; }
  .side:not(.away) { flex-direction: row-reverse; text-align: right; }
  .side.away { flex-direction: row; text-align: left; }
  .side img { width: 18px; height: 18px; object-fit: contain; flex: none; }
  .side .nm { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .sc { font-variant-numeric: tabular-nums; font-weight: 700; min-width: 2.6rem; text-align: center; }
  .sc.pending { color: var(--sports-muted); font-weight: 400; }
  /* Tennis (Flashscore layout): two stacked player rows with per-set games; the
     winner's name and games in bold/full colour. */
  .tennis-fx { display: flex; flex-direction: column; justify-content: center; gap: 0.25rem; min-width: 0; }
  .tennis-fx .tp { display: flex; align-items: center; justify-content: space-between; gap: 0.6rem; min-width: 0; }
  .tennis-fx .tp .nm { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: var(--sports-muted); }
  .tennis-fx .tp.win .nm { color: var(--sports-fg); font-weight: 700; }
  .tennis-fx .tsets { display: flex; gap: 0.35rem; flex: none; font-variant-numeric: tabular-nums; color: var(--sports-muted); }
  .tennis-fx .tp.win .tsets { color: var(--sports-fg); font-weight: 600; }
  .tennis-fx .tg { min-width: 0.85rem; text-align: center; }
  /* Volleyball (SofaScore layout): same stacked rows, but each side also shows
     the bold sets-won total before its per-set points. Name flexes so the total
     and set columns line up on the right across both rows. */
  .tennis-fx.vb-fx .tp .nm { flex: 1 1 auto; }
  .tennis-fx.vb-fx .tt {
    flex: none; min-width: 1.1rem; margin-right: 0.15rem; text-align: center;
    font-variant-numeric: tabular-nums; font-weight: 700; color: var(--sports-muted);
  }
  .tennis-fx.vb-fx .tp.win .tt { color: var(--sports-fg); }
  /* Odds as three boxed 1 / X / 2 cells side by side (the betbrothers.es look):
     a small outcome label above the price, each in its own bordered box. */
  .odds { display: flex; gap: 0.3rem; }
  .cell {
    min-width: 2.9rem; text-align: center; padding: 0.22rem 0.4rem;
    border: 1px solid var(--sports-border); border-radius: 7px; background: var(--sports-bg);
    font-variant-numeric: tabular-nums; text-decoration: none; color: var(--sports-fg);
    font-size: 0.82rem; line-height: 1.25;
  }
  .cell .k { display: block; font-size: 0.6rem; color: var(--sports-muted); }
  .cell.best { background: #fde68a; border-color: #f5cf5b; color: #111318; }
  .cell.best .k { color: #7a5b00; }
  .cell.empty { color: var(--sports-muted); }
  /* Date section header for the all-dates calendar view. */
  .date-head {
    display: flex; align-items: baseline; gap: 0.5rem; padding: 0.6rem 1rem 0.35rem;
    font-weight: 700; font-size: 0.9rem;
  }
  .date-head .count { color: var(--sports-muted); font-weight: 400; font-size: 0.78rem; }
  .more { display: block; width: calc(100% - 2rem); margin: 0.6rem 1rem; padding: 0.5rem;
    border: 1px solid var(--sports-border); border-radius: 8px; background: var(--sports-bg);
    color: var(--sports-fg); font: inherit; cursor: pointer; }

  /* Narrow container (mobile embed) — the betbrothers.es card look: the kickoff
     time on its own line, the fixture centered below it with each crest tucked
     inboard next to the score and the team names on the outside, then the odds
     boxes with the bookmaker on a line beneath. */
  @container (max-width: 540px) {
    .row {
      grid-template-columns: 1fr auto auto 1fr;
      grid-template-areas: "time time time time" "fx fx fx fx" ". odds bk .";
      row-gap: 0.6rem; column-gap: 0.6rem; padding: 0.85rem 1rem;
      align-items: center;
    }
    .row .time { grid-area: time; text-align: left; }
    .row .fx {
      grid-area: fx; display: flex; justify-content: center; align-items: center;
      gap: 0.5rem; overflow: hidden;
    }
    .row .fx .side { flex: 0 1 auto; min-width: 0; }
    /* Crests inboard: home shows "name crest", away shows "crest name". */
    .row .fx .side:not(.away) { flex-direction: row-reverse; text-align: right; }
    .row .fx .side.away { flex-direction: row; text-align: left; }
    .row .fx .sc { min-width: auto; padding: 0 0.35rem; }
    /* Odds boxes + bookmaker centered as a pair on their own row. */
    .row .odds { grid-area: odds; justify-content: flex-end; }
    .row .bk { grid-area: bk; justify-content: flex-start; min-width: 0; }
    .cell { min-width: 2.7rem; }

    /* Toolbar: the desktop layout centres the date-nav with margin:auto and pins
       the results toggle absolute-right, which overlap on a phone. Stack instead:
       the league dropdown takes a full row, then ‹ date › and the toggle share the
       next row. */
    .bar { flex-wrap: wrap; gap: 0.4rem 0.5rem; padding: 0.6rem 0.8rem; }
    .lg-select { flex: 1 1 100%; min-width: 0; }
    .datenav { margin: 0; flex: 1 1 auto; justify-content: flex-start; }
    .datebtn { white-space: nowrap; }
    .toggle { position: static; transform: none; margin-left: auto; }
    .picker { left: 0.8rem; right: 0.8rem; width: auto; }
  }
`

interface FootballMatch {
  id: string
  date?: string
  time?: string
  status?: string
  home: string
  away: string
  homeLogo?: string | null
  awayLogo?: string | null
  homeScore?: number | null
  awayScore?: number | null
  /** Per-match league name; the v2 single-league feed omits it (one league per
   *  page), so it's optional and drives the "Todas las ligas" dropdown only when
   *  present (e.g. a future multi-league feed). */
  league?: string
  odds?: { home: number | null; draw: number | null; away: number | null } | null
}
interface FootballResponse {
  leagueName: string
  matches: FootballMatch[]
}
interface BookmakerResponse {
  oddsFormat: OddsFormat
  brand: { name?: string; logo?: string | null; link?: string | null } | null
}

// Trailing-dot tolerant ("Pen.", "Canc.") — GoalServe abbreviates cup results.
const FINISHED = /^(ft|aet|pen|finished|aban|awarded|postp|canc)\.?$/i
const SCHEDULED = /^(ns|not started|tbd|postp\.?|canc\.?)$/i
function isLive(status: string | undefined): boolean {
  const s = (status ?? '').trim()
  if (!s || FINISHED.test(s) || SCHEDULED.test(s)) return false
  return !/^\d{1,2}:\d{2}$/.test(s)
}

/** Day key yyyy-mm-dd → "dd/mm/yyyy" for the date section header. */
function ddmmyyyy(key: string): string {
  const [y, m, d] = key.split('-')
  return y && m && d ? `${d}/${m}/${y}` : key
}

/** 42-cell month grid (6 weeks, Sunday-first) around month m of year y. */
function monthGrid(y: number, m: number): { key: string; day: number; inMonth: boolean }[] {
  const startDow = new Date(Date.UTC(y, m, 1)).getUTCDay()
  const start = Date.UTC(y, m, 1 - startDow)
  const cells = []
  for (let i = 0; i < 42; i++) {
    const d = new Date(start + i * 86_400_000)
    const key = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`
    cells.push({ key, day: d.getUTCDate(), inMonth: d.getUTCMonth() === m })
  }
  return cells
}
const PICKER_LOCALE: Record<string, string> = {
  en: 'en-GB', es: 'es-ES', de: 'de-DE', fr: 'fr-FR', pt: 'pt-PT', pl: 'pl-PL',
  ru: 'ru-RU', el: 'el-GR', hr: 'hr-HR', sr: 'sr-RS', bs: 'bs-BA', lv: 'lv-LV',
  uk: 'en-GB', ie: 'en-IE', mx: 'es-MX', pe: 'es-PE',
}
function monthLabel(y: number, m: number, lang: string): string {
  const s = new Date(Date.UTC(y, m, 1)).toLocaleDateString(PICKER_LOCALE[lang] ?? 'en-GB', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  })
  return s.charAt(0).toUpperCase() + s.slice(1)
}

interface MatchOdds {
  home: number | null
  draw: number | null
  away: number | null
}
function hasOdds(o: MatchOdds | undefined | null): o is MatchOdds {
  return !!o && (o.home != null || o.away != null)
}
/** "dd.MM.yyyy" + "HH:mm" → an ISO instant (assumed UTC) for name+date matching. */
function isoOf(date?: string, time?: string): string {
  if (!date) return ''
  const [d, m, y] = date.split('.')
  const [hh = '00', mm = '00'] = (time ?? '').split(':')
  return y && m && d
    ? `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}T${hh.padStart(2, '0')}:${mm.padStart(2, '0')}:00Z`
    : ''
}
/** Feed time (UTC) → wall-clock time in the site's zone (see siteTimeZone). */
function localTime(date: string | undefined, time: string | undefined, lang: string, tz?: string): string {
  const iso = isoOf(date, time)
  if (!iso) return time ?? ''
  return kickoffTime(new Date(iso), lang, tz) || (time ?? '')
}

/** "dd.MM.yyyy" → a day key yyyy-mm-dd (sortable, timezone-free). */
function dayKey(date: string | undefined): string {
  if (!date) return ''
  const [d, m, y] = date.split('.')
  return y && m && d ? `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}` : ''
}
function number(value: string | undefined, fallback: number): number {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

export function MatchCenter({
  league = '1399',
  lang = 'es',
  heading,
  refresh,
  apiBase,
  sport = 'football',
}: MatchCenterProps) {
  const base = resolveApiBase(apiBase)
  const refreshSeconds = number(refresh, 60)
  const client = new SportsApiClient(base)
  const t = useLabels(base, lang)
  const tz = useTimeZone()

  // Basketball is a 2-way market (no draw) served by the betbrothers-basket API;
  // football is 1X2 from betbrothers v2. Both normalise to FootballResponse.
  const isBasket = sport === 'basketball'
  const oddsCat = isBasket ? 'basket_10' : 'soccer_10'

  // `league` accepts ONE id or a comma-separated list: the combined centres
  // ("todo el fútbol", "todo el baloncesto") pass every league we carry, each
  // feed is fetched in parallel and its matches tagged with that feed's league
  // name — which is what drives the "Todas las ligas" dropdown and the league
  // header rows below.
  const leagueIds = league
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean)
  const leagueKey = leagueIds.join(',')

  const { data, error, loading } = useFeed(
    (signal): Promise<FootballResponse> => {
      if (leagueIds.length <= 1) {
        const id = leagueIds[0] ?? league
        return isBasket
          ? client.basketballMatches<FootballResponse>(id, signal)
          : client.footballMatches<FootballResponse>(id, lang, signal)
      }
      // Several leagues: one combined request. The API merges the per-league
      // feeds, tags every match with its competition and trims to the window
      // the calendar can show (fetching each league here was ~1.6 MB).
      return isBasket
        ? client.basketballMatchesCombined<FootballResponse>(leagueIds, signal)
        : client.footballMatchesCombined<FootballResponse>(leagueIds, lang, signal)
    },
    [base, leagueKey, lang, sport],
    refreshSeconds,
  )
  const { data: display } = useFeed(
    (signal) => client.bookmaker<BookmakerResponse>(lang, 'match-center', signal),
    [base, lang],
    0,
  )

  const format: OddsFormat = display?.oddsFormat ?? 'decimal'
  const brand = display?.brand ?? null
  const link = brand?.link ?? undefined
  // Combined centres carry a league per match (v2 / basket-API names are English:
  // "Spanish Second Division", "Euroleague"). Localize once, here, so the
  // dropdown, the filter, the sort and the league strips all agree.
  const all = (data?.matches ?? []).map((m) =>
    m.league ? { ...m, league: localizeLeague(m.league, lang) } : m,
  )

  // The v2 feed only carries 1X2 for the near gameweeks; for upcoming matches it
  // hasn't priced, fill the gaps from the odds aggregator (GoalServe soccer_10 +
  // Tonybet + Cloudbet) matched by team names + kickoff.
  const gapRefs = all
    .filter((m) => !FINISHED.test(m.status ?? '') && !hasOdds(m.odds))
    .map((m) => ({ id: m.id, home: m.home, away: m.away, dateUtc: isoOf(m.date, m.time) }))
  const gapKey = gapRefs.map((r) => r.id).join(',')
  const { data: filled } = useFeed(
    (signal) =>
      gapRefs.length
        ? client.resolveOdds<Record<string, MatchOdds>>(oddsCat, gapRefs, signal)
        : Promise.resolve({} as Record<string, MatchOdds>),
    [base, gapKey],
    0,
  )
  const oddsOf = (m: FootballMatch): MatchOdds | null =>
    hasOdds(m.odds) ? (m.odds as MatchOdds) : filled?.[m.id] ?? null

  // A schedule leads with what's ahead: finished matches are hidden until asked.
  const [showFinished, setShowFinished] = useState(false)
  const [limit, setLimit] = useState(20)
  // League filter for the "Todas las ligas" dropdown (null = all leagues).
  const [leagueFilter, setLeagueFilter] = useState<string | null>(null)
  // Date picker: which day is filtered to (null = all), the open state, and the
  // month currently shown in the popup.
  const [fromKey, setFromKey] = useState<string | null>(null)
  const [pickerOpen, setPickerOpen] = useState(false)
  const now0 = new Date()
  const [viewMonth, setViewMonth] = useState({ y: now0.getFullYear(), m: now0.getMonth() })
  const matchDays = new Set(all.map((mm) => dayKey(mm.date)).filter(Boolean))

  // A match is "done" (hidden from the default schedule) when it's flagged
  // finished, OR carries a final score and isn't live, OR its date is already
  // past — the v2 feed's cup/qualifier fixtures often have a non-"FT" status yet
  // are clearly over, which used to leak weeks-old results to the top.
  const todayKey = todayKeyIn(lang, new Date(), tz)
  const isDone = (m: FootballMatch): boolean => {
    // Past date wins outright — a fixture on an earlier day is over no matter
    // what its status string says (cup ties often read "Pen.", which isLive()
    // otherwise mistakes for in-play, leaking weeks-old results to the top).
    const k = dayKey(m.date)
    if (k !== '' && k < todayKey) return true
    if (FINISHED.test(m.status ?? '')) return true
    if (isLive(m.status)) return false
    return m.homeScore != null && m.awayScore != null
  }

  // "Todas las fechas": all upcoming matches (finished hidden unless asked),
  // oldest first, grouped by date. A picked day shows everything on that date.
  const dateFiltered = all.filter((m) =>
    fromKey ? dayKey(m.date) === fromKey : showFinished || !isDone(m),
  )
  // Leagues present (for the "Todas las ligas" dropdown) + the active filter.
  const leagues = [...new Set(dateFiltered.map((m) => m.league).filter(Boolean))].sort() as string[]
  // Day first, then league (so a combined centre clusters each competition
  // under one header), then kick-off. A single-league centre has one league, so
  // this is the plain chronological order it always had.
  const upcoming = dateFiltered
    .filter((m) => !leagueFilter || m.league === leagueFilter)
    .sort((a, b) =>
      `${dayKey(a.date)} ${a.league ?? ''} ${a.time ?? ''}`.localeCompare(
        `${dayKey(b.date)} ${b.league ?? ''} ${b.time ?? ''}`,
      ),
    )
  // Days that have matches, sorted — the ‹ › arrows step through them.
  const sortedDays = [...matchDays].sort()
  const stepDay = (dir: 1 | -1) => {
    if (!fromKey) {
      setFromKey(dir === 1 ? sortedDays[0] : sortedDays[sortedDays.length - 1])
    } else {
      const i = sortedDays.indexOf(fromKey)
      const next = sortedDays[i + dir]
      setFromKey(next ?? (dir === -1 ? null : fromKey))
    }
    setLimit(20)
  }
  const shown = upcoming.slice(0, limit)
  const dayGroups: { day: string; matches: FootballMatch[] }[] = []
  for (const m of shown) {
    const k = dayKey(m.date)
    const last = dayGroups[dayGroups.length - 1]
    if (last && last.day === k) last.matches.push(m)
    else dayGroups.push({ day: k, matches: [m] })
  }
  const leagueName = data?.leagueName ?? heading ?? ''

  // Three boxed 1 / X / 2 cells (betbrothers.es look): a small outcome label
  // above the price, the favourite (lowest price) highlighted yellow.
  const cell = (label: string, price: number | null | undefined, best: boolean) => {
    const text = price != null ? formatOdds(price, format) : ''
    const cls = `cell${best && text ? ' best' : ''}${text ? '' : ' empty'}`
    const inner = (
      <>
        <span class="k">{label}</span>
        {text || '—'}
      </>
    )
    return link && text ? (
      <a class={cls} href={link} target="_blank" rel="sponsored noopener">
        {inner}
      </a>
    ) : (
      <span class={cls}>{inner}</span>
    )
  }
  const lowest = (vals: (number | null | undefined)[]): number | null => {
    const present = vals.filter((v): v is number => v != null && Number.isFinite(v) && v > 1)
    return present.length ? Math.min(...present) : null
  }

  // The per-locale bookmaker at the right of every priced row — a link to the
  // affiliate URL, like the odds cells.
  const bookmaker = () => {
    const inner = brand?.logo ? (
      <img src={brand.logo} alt={brand.name ?? ''} />
    ) : (
      brand?.name ?? ''
    )
    if (!brand?.logo && !brand?.name) return <span class="bk" />
    return link ? (
      <a class="bk" href={link} target="_blank" rel="sponsored noopener">
        {inner}
      </a>
    ) : (
      <span class="bk">{inner}</span>
    )
  }

  return (
    <div class="sports-card">
      <div class="sports-head">
        <span>{heading ?? (leagueName || 'Match center')}</span>
      </div>

      <div class="bar">
        {leagues.length > 1 && (
          <select
            class="lg-select"
            value={leagueFilter ?? ''}
            onChange={(e) => setLeagueFilter((e.target as HTMLSelectElement).value || null)}
          >
            <option value="">{t('allLeagues', 'Todas las ligas')}</option>
            {leagues.map((l) => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </select>
        )}
        <div class="datenav">
          <button
            class="nav"
            aria-label="prev"
            disabled={sortedDays.length === 0 || fromKey === sortedDays[0]}
            onClick={() => stepDay(-1)}
          >
            ‹
          </button>
          <button class="datebtn" onClick={() => setPickerOpen((o) => !o)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="3" y="4" width="18" height="18" rx="2" />
              <path d="M16 2v4M8 2v4M3 10h18" />
            </svg>
            {fromKey ? ddmmyyyy(fromKey) : t('allDates', 'Todas las fechas')}
          </button>
          <button
            class="nav"
            aria-label="next"
            disabled={sortedDays.length === 0 || fromKey === sortedDays[sortedDays.length - 1]}
            onClick={() => stepDay(1)}
          >
            ›
          </button>
        </div>
        <label class="toggle">
          <input
            type="checkbox"
            checked={showFinished}
            onChange={(e) => setShowFinished((e.target as HTMLInputElement).checked)}
          />
          {t('results', 'Results')}
        </label>
        {pickerOpen && (
          <div class="picker">
            <div class="pk-head">
              <button onClick={() => setViewMonth((v) => (v.m === 0 ? { y: v.y - 1, m: 11 } : { y: v.y, m: v.m - 1 }))}>‹</button>
              <span>{monthLabel(viewMonth.y, viewMonth.m, lang)}</span>
              <button onClick={() => setViewMonth((v) => (v.m === 11 ? { y: v.y + 1, m: 0 } : { y: v.y, m: v.m + 1 }))}>›</button>
            </div>
            <div class="pk-grid pk-dow">
              {Array.from({ length: 7 }, (_, i) =>
                new Date(Date.UTC(2023, 0, 1 + i)).toLocaleDateString(PICKER_LOCALE[lang] ?? 'en-GB', { weekday: 'short', timeZone: 'UTC' }),
              ).map((w) => (
                <span key={w}>{w}</span>
              ))}
            </div>
            <div class="pk-grid">
              {monthGrid(viewMonth.y, viewMonth.m).map((c) => (
                <button
                  key={c.key}
                  class={`pk-day${c.inMonth ? '' : ' out'}${matchDays.has(c.key) ? ' has' : ''}${fromKey === c.key ? ' sel' : ''}`}
                  disabled={!matchDays.has(c.key)}
                  onClick={() => {
                    setFromKey(c.key)
                    setPickerOpen(false)
                    setLimit(20)
                  }}
                >
                  {c.day}
                </button>
              ))}
            </div>
            <button
              class="pk-all"
              onClick={() => {
                setFromKey(null)
                setPickerOpen(false)
              }}
            >
              {t('allDates', 'Todas las fechas')}
            </button>
          </div>
        )}
      </div>

      {loading && <div class="sports-note">{t('loading', 'Loading…')}</div>}
      {!loading && error && (
        <div class="sports-note error">{t('matchesUnavailable', 'Matches are unavailable right now.')}</div>
      )}
      {!loading && !error && shown.length === 0 && (
        <div class="sports-note">{t('noMatches', 'No matches this day.')}</div>
      )}

      {dayGroups.map((g) => (
        <div key={g.day}>
          <div class="date-head">
            {ddmmyyyy(g.day)}
            <span class="count">
              {g.matches.length === 1
                ? t('oneMatch', '1 partido')
                : `${g.matches.length} ${t('nMatches', 'partidos')}`}
            </span>
          </div>
          {g.matches.map((m, i) => {
            const live = isLive(m.status)
            // Multi-league centre: a league strip whenever the league changes
            // within the day (single-league pages never show one).
            const header =
              leagues.length > 1 && m.league && m.league !== g.matches[i - 1]?.league ? m.league : null
            const played = live || FINISHED.test(m.status ?? '')
            return (
              <Fragment key={m.id}>
              {header && (
                <div class="league">
                  <span class="lg">{header}</span>
                </div>
              )}
              <div class={live ? 'row live' : 'row'} key={m.id}>
                <span class="time">
                  {live ? <span class="st">{m.status}</span> : played ? m.status : localTime(m.date, m.time, lang, tz)}
                </span>
                <span class="fx">
                  <span class="side">
                    <TeamCrest src={m.homeLogo} name={m.home} />
                    <span class="nm">{m.home}</span>
                  </span>
                  <span class={played ? 'sc' : 'sc pending'}>
                    {played ? `${m.homeScore ?? 0} : ${m.awayScore ?? 0}` : t('vs', 'vs')}
                  </span>
                  <span class="side away">
                    <TeamCrest src={m.awayLogo} name={m.away} />
                    <span class="nm">{m.away}</span>
                  </span>
                </span>
                {played ? (
                  <span class="odds" />
                ) : (
                  (() => {
                    const o = oddsOf(m)
                    const best = lowest(isBasket ? [o?.home, o?.away] : [o?.home, o?.draw, o?.away])
                    return (
                      <span class="odds">
                        {cell('1', o?.home, o?.home === best)}
                        {!isBasket && cell('X', o?.draw, o?.draw === best)}
                        {cell('2', o?.away, o?.away === best)}
                      </span>
                    )
                  })()
                )}
                {bookmaker()}
              </div>
              </Fragment>
            )
          })}
        </div>
      ))}

      {upcoming.length > shown.length && (
        <button class="more" onClick={() => setLimit((l) => l + 20)}>
          {t('loadMore', 'Ver más')}
        </button>
      )}
    </div>
  )
}
